import { BaseController } from '../../../../base_controller';
import zoomSdk, { ConfigResponse } from '@zoom/appssdk';
import { MESSAGES } from '../integration_component/integration_component_controller';
import {
  configureZoomSdk,
  subscribeToParticipantChanges,
  supportsZoomApi,
} from '../zoom_sdk';

export default class extends BaseController {
  static targets = [
    'inviteButton',
    'errorMessage',
    'statusMessage',
    'startVoteButton',
    'closeWindowButton',
    'participantsMessage',
  ];

  declare inviteButtonTarget: HTMLButtonElement;
  declare startVoteButtonTarget: HTMLButtonElement;
  declare closeWindowButtonTarget: HTMLButtonElement;
  declare errorMessageTarget: HTMLElement;
  declare statusMessageTarget: HTMLElement;
  declare participantsMessageTarget: HTMLElement;
  declare hasInviteButtonTarget: boolean;

  static values = {
    userHost: Boolean,
    inMeeting: Boolean,
    macWorkaroundNeeded: Boolean,
    votersCount: Number,
  };

  declare userHostValue: boolean;
  declare inMeetingValue: boolean;
  declare macWorkaroundNeededValue: boolean;
  declare votersCountValue: number;
  private unsubscribeParticipantChanges?: () => void;
  private participantsCount?: number;

  async connect(): Promise<void> {
    this.runMacWorkaroundIfNeeded();
    if (!this.userHostValue || !this.inMeetingValue || !this.hasInviteButtonTarget) return;

    this.setButtonsDisabled(true);

    try {
      const config = await configureZoomSdk();
      if (!this.element.isConnected) return;

      this.logger('Zoom JS SDK Configuration', config);
      this.configureButtons(config);
      this.setupListeners(config);
      await this.updateMeetingParticipants(config);
    } catch (error) {
      console.error(error);
      this.showErrorMessage('Could not connect to Zoom. Reopen the app and try again.');
    }
  }

  disconnect(): void {
    this.unsubscribeParticipantChanges?.();
    this.unsubscribeParticipantChanges = undefined;
  }

  public async inviteParticipants(): Promise<void> {
    this.hideFeedback();
    this.disableInviteButton();
    let config: ConfigResponse;

    try {
      config = await configureZoomSdk();
      if (!supportsZoomApi(config, 'sendAppInvitationToAllParticipants')) {
        this.showErrorMessage('This Zoom client cannot invite participants to the app.');
        return;
      }

      if (this.participantsCount === 0) {
        this.showStatusMessage('There are no other meeting participants to invite.');
        return;
      }

      const response = await zoomSdk.sendAppInvitationToAllParticipants();
      if (!response.invitationUUID) {
        this.showErrorMessage('Zoom did not confirm the invitation request. Check before retrying.');
        return;
      }
      this.showStatusMessage('Zoom invitation request sent. Participant acceptance is not confirmed.');
    } catch (error) {
      console.error(error);
      this.showErrorMessage('Zoom could not send the invitation request. Try again.');
    } finally {
      this.inviteButtonTarget.disabled = !config ||
        !supportsZoomApi(config, 'sendAppInvitationToAllParticipants');
    }
  }

  public async sendFocusAppMessage(): Promise<void> {
    await this.sendAppMessage(
      MESSAGES.FOCUS_APP,
      this.startVoteButtonTarget,
      'Show request sent to Zoom. Only participants with the app open can receive it; receipt is not confirmed.'
    );
  }

  public async sendUnfocusAppMessage(): Promise<void> {
    await this.sendAppMessage(
      MESSAGES.UNFOCUS_APP,
      this.closeWindowButtonTarget,
      'Hide request sent to Zoom. Only participants with the app open can receive it; receipt is not confirmed.'
    );
  }

  // private methods

  private runMacWorkaroundIfNeeded(): void {
    if (this.macWorkaroundNeededValue) {
      // Set a flag in localStorage to prevent infinite refresh loops
      const hasAttemptedRefresh = localStorage.getItem('zoomContextRefreshAttempted');

      if (!hasAttemptedRefresh) {
        localStorage.setItem('zoomContextRefreshAttempted', 'true');

        // Wait a brief moment to ensure any initial loading is complete
        setTimeout(() => {
          // Perform the refresh; hopefully the context will be available after
          window.location.reload();
        }, 500);
      } else {
        // We've already tried refreshing but still don't have context
        // Clear the flag for next session
        localStorage.removeItem('zoomContextRefreshAttempted');

        console.error("Zoom context still missing after refresh attempt");
        this.showErrorMessage('There was an error connecting to Zoom. Please restart the application.');
      }
    } else {
      if (localStorage.getItem('zoomContextRefreshAttempted')) {
        localStorage.removeItem('zoomContextRefreshAttempted');
      }
    }
  }

  private configureButtons(config: ConfigResponse): void {
    this.inviteButtonTarget.disabled = !supportsZoomApi(config, 'sendAppInvitationToAllParticipants');
    const canSend = supportsZoomApi(config, 'sendMessage');
    this.startVoteButtonTarget.disabled = !canSend;
    this.closeWindowButtonTarget.disabled = !canSend;

    if (!canSend || this.inviteButtonTarget.disabled) {
      this.showErrorMessage('Some controls are unavailable in this Zoom client.');
    }
  }

  private async updateMeetingParticipants(config: ConfigResponse): Promise<void> {
    if (!supportsZoomApi(config, 'getMeetingParticipants')) {
      this.participantsCount = undefined;
      this.participantsMessageTarget.textContent = 'Meeting participant count unavailable.';
      return;
    }

    try {
      const { participants } = await zoomSdk.getMeetingParticipants();
      const participantsCount = Math.max(0, participants.length - 1);
      this.participantsCount = participantsCount;
      this.participantsMessageTarget.textContent =
        `Meeting Participants: ${participantsCount}/${this.votersCountValue}`;
    } catch (error) {
      console.error(error);
      this.participantsCount = undefined;
      this.participantsMessageTarget.textContent = 'Meeting participant count unavailable.';
    }
  }

  private setupListeners(config: ConfigResponse): void {
    if (!supportsZoomApi(config, 'onParticipantChange') || this.unsubscribeParticipantChanges) return;

    this.unsubscribeParticipantChanges = subscribeToParticipantChanges(() => {
      this.updateMeetingParticipants(config);
    });
  }

  private async sendAppMessage(id: string, button: HTMLButtonElement, successMessage: string): Promise<void> {
    this.hideFeedback();
    button.disabled = true;
    let config: ConfigResponse;

    try {
      config = await configureZoomSdk();
      if (!supportsZoomApi(config, 'sendMessage')) {
        this.showErrorMessage('This Zoom client cannot send app messages.');
        return;
      }

      if (this.participantsCount === 0) {
        this.showStatusMessage('There are no other meeting participants to receive this request.');
        return;
      }

      const payload = { id };
      const response = await zoomSdk.sendMessage({ payload });
      if (response.message !== 'Success') throw new Error('Zoom did not send the app message');
      this.logger('New message sent:', payload);
      this.showStatusMessage(successMessage);
    } catch (error) {
      console.error(error);
      this.showErrorMessage('Zoom could not send the request. Try again.');
    } finally {
      button.disabled = !config || !supportsZoomApi(config, 'sendMessage');
    }
  }

  protected logger(message, details): void {
    const finalMessage = `[ZoomIntegration] ${message}`;

    super.logger(finalMessage, details, '#b526b0');
  }

  private disableInviteButton(): void {
    this.inviteButtonTarget.disabled = true;
  }

  private setButtonsDisabled(disabled: boolean): void {
    this.inviteButtonTarget.disabled = disabled;
    this.startVoteButtonTarget.disabled = disabled;
    this.closeWindowButtonTarget.disabled = disabled;
  }

  private showErrorMessage(message: string): void {
    this.statusMessageTarget.classList.add('hide');
    this.errorMessageTarget.classList.remove('hide');
    this.errorMessageTarget.textContent = message;
  }

  private showStatusMessage(message: string): void {
    this.errorMessageTarget.classList.add('hide');
    this.statusMessageTarget.classList.remove('hide');
    this.statusMessageTarget.textContent = message;
  }

  private hideFeedback(): void {
    this.errorMessageTarget.classList.add('hide');
    this.statusMessageTarget.classList.add('hide');
  }
}
